"use client";

import type React from "react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import styles from "./gradient-border.module.css";
export type ColorVariant = "default" | "sunset" | "ocean" | "forest" | "neon" | "purple" | "fire" | "ice" | "galaxy" | "aurora" | "cyber" | "dusk" | "dawn" | "inferno" | "blaze" | "ember";
export interface CustomColors {
  primary: string;
  secondary: string;
  accent?: string;
}
export type DotSpacing = "tight" | "normal" | "loose" | "wide";
interface GradientBorderProps {
  /** The content to be wrapped with the gradient border */
  children: ReactNode;

  /**
   * Tailwind CSS classes for styling and sizing
   * @example "w-96 h-64 rounded-xl" for a 384x256px container with extra-large border radius
   */
  className?: string;

  // ========== DEPRECATED PROPS (use className instead) ==========
  /** @deprecated Use className with w-* classes instead */
  width?: number | string;
  /** @deprecated Use className with h-* classes instead */
  height?: number | string;

  // ========== COLOR AND VISUAL VARIANTS ==========
  /**
   * Predefined color scheme for the gradient border
   * @default "default"
   * Available variants: "default", "sunset", "ocean", "forest", "neon", "purple",
   * "fire", "ice", "galaxy", "aurora", "cyber", "dusk", "dawn", "inferno", "blaze", "ember"
   */
  variant?: ColorVariant;

  /**
   * Custom color configuration (overrides variant)
   * @example { primary: "#ff0000", secondary: "#0000ff", accent: "#00ff00" }
   */
  customColors?: CustomColors;

  // ========== ANIMATION CONTROLS ==========
  /**
   * Speed of the gradient animation
   * @default "normal"
   * - "slow": 12 seconds per rotation
   * - "normal": 8 seconds per rotation
   * - "fast": 4 seconds per rotation
   */
  animationSpeed?: "slow" | "normal" | "fast";

  /**
   * Visual intensity of the glow effects
   * @default "high"
   * - "low": Subtle glow with reduced opacity
   * - "medium": Moderate glow intensity
   * - "high": Strong glow effects
   * - "ultra": Maximum glow with enhanced blur
   */
  intensity?: "low" | "medium" | "high" | "ultra";

  // ========== BACKGROUND CONTROLS ==========
  /**
   * Background color for the dotted pattern area
   * @default "#111111"
   * @example "#ff0000" for red background
   * Accepts any valid CSS color value (hex, rgb, hsl, etc.)
   */
  backgroundColor?: string;

  /**
   * Opacity of the background color
   * @default 0.67
   * @min 0 (fully transparent)
   * @max 1 (fully opaque)
   */
  backgroundOpacity?: number;

  /**
   * Starting point of the radial background gradient (percentage from center)
   * @default 20
   * @min 0 (starts immediately from center)
   * @max 100 (starts from edge)
   */
  backgroundStart?: number;

  /**
   * Ending point of the radial background gradient (percentage from center)
   * @default 50
   * @min 0 (ends at center)
   * @max 100 (ends at edge)
   */
  backgroundEnd?: number;

  /**
   * Use solid background color instead of radial gradient
   * @default false
   * When true, ignores backgroundStart and backgroundEnd
   */
  backgroundFull?: boolean;

  // ========== DOT PATTERN CONTROLS ==========
  /**
   * Spacing between dots in the background pattern
   * @default "normal"
   * Preset values: "tight" (2px), "normal" (3px), "loose" (4px), "wide" (6px)
   * Or custom number for pixel spacing (e.g., 5 for 5px spacing)
   */
  dotSpacing?: DotSpacing | number;

  // ========== BORDER CONTROLS ==========
  /**
   * Opacity of the border element background
   * @default 0.2
   * @min 0 (fully transparent)
   * @max 1 (fully opaque)
   */
  borderOpacity?: number;

  /**
   * Color of the border element
   * @default "#000000"
   * Accepts any valid CSS color value
   */
  borderColor?: string;

  // ========== LAYOUT CONTROLS ==========
  /**
   * Opacity of the main page background
   * @default 1
   * @min 0 (fully transparent)
   * @max 1 (fully opaque)
   */
  pageBackgroundOpacity?: number;

  /**
   * Opacity of the content area background
   * @default 1
   * @min 0 (fully transparent)
   * @max 1 (fully opaque)
   */
  contentBackgroundOpacity?: number;

  /**
   * Show colored borders around elements for debugging layout issues
   * @default false
   */
  debugMode?: boolean;

  /**
   * Optimize rendering for embedded use (reduces some effects)
   * @default false
   */
  embedded?: boolean;

  // ========== ENHANCED TRACING CONTROLS ==========
  /**
   * Enable enhanced tracing mode for better rectangular border coverage
   * @default false
   * Reduces original effects slightly to highlight custom tracing settings
   */
  enhancedTracing?: boolean;

  /**
   * Custom inset value for gradient coverage
   * @default undefined (uses automatic values based on shape)
   * @example "-150%" for wide rectangles, "-300%" for ultra-wide shapes
   * Larger negative values provide better coverage for rectangular shapes
   * Common values: "-30%" (squares), "-150%" (rectangles), "-200%" (wide), "-300%" (ultra-wide)
   */
  customInset?: string;

  /**
   * Custom gradient direction
   * @default undefined (uses automatic direction based on shape)
   * @example "90deg" for horizontal, "0deg" for vertical, "-45deg" for diagonal
   * - "-45deg": Diagonal (perfect for squares)
   * - "90deg": Horizontal (good for landscape rectangles)
   * - "0deg": Vertical (good for portrait rectangles)
   * - "45deg": Reverse diagonal
   * Accepts any valid CSS angle value (deg, rad, turn, grad)
   */
  customGradientDirection?: string;

  /**
   * Enable rotating gradient bars for perfect corner tracing
   * @default false
   * Adds physical rotating bars that sweep around the perimeter
   * Recommended for ultra-wide rectangles or when perfect corner coverage is critical
   */
  rotatingBars?: boolean;

  /**
   * Opacity of the rotating gradient bars
   * @default 0.6
   * @min 0 (invisible)
   * @max 1 (fully opaque)
   * Only applies when rotatingBars is enabled
   */
  rotatingBarOpacity?: number;

  /**
   * Disable all animations for better mobile performance
   * @default false
   * When true, all spinning and rotating animations are disabled
   */
  disableAnimations?: boolean;

  /**
   * Pause the gradient rotation animation at a specific position
   * @default false
   * When true, stops the rotation animation but keeps all other effects
   * Use with pausePosition to control where the animation stops
   */
  pauseAnimation?: boolean;

  /**
   * Position where the animation should be paused (in degrees)
   * @default 0
   * @min 0
   * @max 360
   * Only applies when pauseAnimation is true
   * 0deg = top, 90deg = right, 180deg = bottom, 270deg = left
   */
  pausePosition?: number;
}
const colorVariants: Record<ColorVariant, CustomColors> = {
  default: {
    primary: "#f50",
    secondary: "#05f",
    accent: "#fc9"
  },
  // default: {
  //   primary: "#05f",
  //   secondary: "#f50",
  //   accent: "#fc9",
  // },
  sunset: {
    primary: "#ff6b35",
    secondary: "#f7931e",
    accent: "#ffcc02"
  },
  ocean: {
    primary: "#00d4ff",
    secondary: "#0099cc",
    accent: "#66e0ff"
  },
  forest: {
    primary: "#4ade80",
    secondary: "#22c55e",
    accent: "#86efac"
  },
  neon: {
    primary: "#ff0080",
    secondary: "#00ff80",
    accent: "#8000ff"
  },
  purple: {
    primary: "#8b5cf6",
    secondary: "#a855f7",
    accent: "#c084fc"
  },
  fire: {
    primary: "#dc2626",
    secondary: "#ea580c",
    accent: "#fbbf24"
  },
  ice: {
    primary: "#06b6d4",
    secondary: "#0891b2",
    accent: "#67e8f9"
  },
  galaxy: {
    primary: "#7c3aed",
    secondary: "#db2777",
    accent: "#f59e0b"
  },
  aurora: {
    primary: "#10b981",
    secondary: "#3b82f6",
    accent: "#8b5cf6"
  },
  cyber: {
    primary: "#00ff41",
    secondary: "#ff0080",
    accent: "#00ffff"
  },
  dusk: {
    primary: "#6366f1",
    secondary: "#ec4899",
    accent: "#f59e0b"
  },
  dawn: {
    primary: "#f97316",
    secondary: "#fbbf24",
    accent: "#fde047"
  },
  inferno: {
    primary: "#b91c1c",
    secondary: "#dc2626",
    accent: "#f97316"
  },
  blaze: {
    primary: "#ea580c",
    secondary: "#f97316",
    accent: "#fbbf24"
  },
  ember: {
    primary: "#dc2626",
    secondary: "#f59e0b",
    accent: "#fde047"
  }
};
const animationSpeeds = {
  slow: "12s",
  normal: "8s",
  fast: "4s"
};
const dotSpacings = {
  tight: "2px",
  normal: "3px",
  loose: "4px",
  wide: "6px"
};
export default function GradientBorder({
  children,
  className = "",
  width,
  height,
  variant = "default",
  customColors,
  animationSpeed = "normal",
  backgroundColor = "#111111",
  backgroundOpacity = 0.67,
  dotSpacing = "normal",
  backgroundStart = 20,
  backgroundEnd = 50,
  backgroundFull = false,
  borderOpacity = 0.2,
  borderColor = "#000000",
  pageBackgroundOpacity = 1,
  contentBackgroundOpacity = 1,
  debugMode = false,
  intensity = "high",
  embedded = false,
  // NEW: Enhanced tracing controls
  enhancedTracing = false,
  customInset,
  customGradientDirection,
  rotatingBars = false,
  rotatingBarOpacity = 0.6,
  disableAnimations = false,
  pauseAnimation = false,
  pausePosition = 0
}: GradientBorderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const colors = customColors || colorVariants[variant];
  const speed = animationSpeeds[animationSpeed];

  // Handle dot spacing - can be preset or custom number
  const spacing = typeof dotSpacing === "number" ? `${dotSpacing}px` : dotSpacings[dotSpacing];

  // Calculate aspect ratio and apply appropriate class - but allow manual override
  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const aspectRatio = rect.width / rect.height;

      // Remove existing aspect ratio classes
      container.classList.remove("aspect-square", "aspect-landscape", "aspect-portrait", "aspect-wide", "aspect-tall");

      // Only apply automatic aspect ratio classes if not using custom settings
      if (!customInset && !customGradientDirection) {
        if (aspectRatio >= 0.9 && aspectRatio <= 1.1) {
          container.classList.add("aspect-square");
        } else if (aspectRatio > 2.5) {
          container.classList.add("aspect-wide");
        } else if (aspectRatio > 1.1) {
          container.classList.add("aspect-landscape");
        } else if (aspectRatio < 0.4) {
          container.classList.add("aspect-tall");
        } else {
          container.classList.add("aspect-portrait");
        }
      }
    }
  }, [className, width, height, customInset, customGradientDirection]);

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, opacity: number) => {
    const cleanHex = hex.replace(/^#/, "");
    const r = Number.parseInt(cleanHex.substring(0, 2), 16);
    const g = Number.parseInt(cleanHex.substring(2, 4), 16);
    const b = Number.parseInt(cleanHex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Create colors with opacity
  const bgColor = hexToRgba(backgroundColor, backgroundOpacity);
  const borderBgColor = hexToRgba(borderColor, borderOpacity);

  // Create the background gradient based on props
  let backgroundGradient;
  if (backgroundFull) {
    backgroundGradient = `radial-gradient(circle at 50% 50%, ${bgColor} 0%, ${bgColor} 100%)`;
  } else {
    backgroundGradient = `radial-gradient(circle at 50% 50%, transparent 0%, transparent ${backgroundStart}%, ${bgColor} ${backgroundEnd}%)`;
  }

  // Set intensity class based on prop
  const intensityClass = styles[`intensity-${intensity}`] || "";

  // Add enhanced tracing class if enabled
  const enhancedTracingClass = enhancedTracing ? styles.enhancedTracing : "";

  // Add rotating bars class if enabled
  const rotatingBarsClass = rotatingBars ? styles.rotatingBarsEnabled : "";

  // Add pause animation class if enabled
  const pauseAnimationClass = pauseAnimation ? styles.pauseAnimation : "";
  const cssVariables = {
    "--gradient-primary": colors.primary,
    "--gradient-secondary": colors.secondary,
    "--gradient-accent": colors.accent || colors.primary,
    "--animation-speed": speed,
    "--backdrop-color": bgColor,
    "--dot-spacing": spacing,
    "--background-gradient": backgroundGradient,
    "--border-background": borderBgColor,
    "--page-background-opacity": pageBackgroundOpacity,
    "--content-background-opacity": contentBackgroundOpacity,
    // NEW: Custom override variables
    "--custom-inset": customInset || "var(--gradient-inset, -30%)",
    "--custom-gradient-direction": customGradientDirection || "var(--gradient-direction, -45deg)",
    "--rotating-bar-opacity": rotatingBarOpacity,
    // NEW: Pause animation controls
    "--pause-animation": pauseAnimation ? "paused" : "running",
    "--pause-position": `${pausePosition}deg`
  } as React.CSSProperties;

  // Check if className contains size classes, if not use width/height props
  const hasClassNameSizing = className.includes("w-") || className.includes("h-") || className.includes("size-") || className.includes("min-h-") || className.includes("max-h-") || className.includes("min-w-") || className.includes("max-w-");

  // Default size if no className sizing and no width/height props
  const defaultSize = !hasClassNameSizing && !width && !height;

  // Inline styles for backward compatibility (only if no className sizing)
  const inlineStyles = hasClassNameSizing ? {} : {
    width: width || (defaultSize ? 400 : undefined),
    height: height || (defaultSize ? 300 : undefined)
  };

  // Determine if we should use intrinsic sizing (size based on content)
  const useIntrinsicSizing = !hasClassNameSizing && !width && !height;
  return <div className={`${styles.container} ${className}`} data-sentry-component="GradientBorder" data-sentry-source-file="gradient-border.tsx">
      {/* SVG Filters */}
      <svg style={{
      position: "absolute",
      width: 0,
      height: 0
    }} data-sentry-element="svg" data-sentry-source-file="gradient-border.tsx">
        <defs data-sentry-element="defs" data-sentry-source-file="gradient-border.tsx">
          <filter id="unopaq" y="-100%" height="300%" x="-100%" width="300%" data-sentry-element="filter" data-sentry-source-file="gradient-border.tsx">
            <feColorMatrix values="1 0 0 0 0 
                    0 1 0 0 0 
                    0 0 1 0 0 
                    0 0 0 5 0" data-sentry-element="feColorMatrix" data-sentry-source-file="gradient-border.tsx" />
          </filter>
          <filter id="unopaq2" y="-100%" height="300%" x="-100%" width="300%" data-sentry-element="filter" data-sentry-source-file="gradient-border.tsx">
            <feColorMatrix values="1 0 0 0 0 
                    0 1 0 0 0 
                    0 0 1 0 0 
                    0 0 0 10 0" data-sentry-element="feColorMatrix" data-sentry-source-file="gradient-border.tsx" />
          </filter>
          <filter id="unopaq3" y="-100%" height="300%" x="-100%" width="300%" data-sentry-element="filter" data-sentry-source-file="gradient-border.tsx">
            <feColorMatrix values="1 0 0 1 0 
                    0 1 0 1 0 
                    0 0 1 1 0 
                    0 0 0 2 0" data-sentry-element="feColorMatrix" data-sentry-source-file="gradient-border.tsx" />
          </filter>
        </defs>
      </svg>

      <div ref={containerRef} className={`${styles.gradientContainer} ${className} ${intensityClass} ${enhancedTracingClass} ${rotatingBarsClass} ${useIntrinsicSizing ? styles.intrinsic : ""} ${pauseAnimationClass}`} style={{
      ...inlineStyles,
      ...cssVariables,
      ...(debugMode && {
        border: "2px solid red"
      })
    }}>
        {/* Original spinning gradient effects (only when animations enabled) */}
        {!disableAnimations && <>
            <div className={`${styles.spin} ${styles.spinBlur}`} style={debugMode ? {
          border: "1px solid blue"
        } : {}}></div>
            <div className={`${styles.spin} ${styles.spinIntense}`} style={debugMode ? {
          border: "1px solid green"
        } : {}}></div>
          </>}

        {/* Rotating gradient bars (only when enabled and animations enabled) */}
        {rotatingBars && !disableAnimations && <>
            <div className={styles.rotatingGradient}></div>
            <div className={styles.rotatingGradientSecondary}></div>
          </>}

        {/* Original backdrop with dotted pattern */}
        <div className={styles.backdrop} style={debugMode ? {
        border: "1px solid yellow"
      } : {}}></div>

        <div className={styles.border} style={debugMode ? {
        border: "1px solid purple"
      } : {}}>
          {!disableAnimations && <div className={`${styles.spin} ${styles.spinInside}`} style={debugMode ? {
          border: "1px solid orange"
        } : {}}></div>}
        </div>
        <div className={styles.content} style={debugMode ? {
        border: "1px solid pink"
      } : {}}>
          {children}
        </div>
      </div>
    </div>;
}